import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ActiveAntsWarehouseCheckInService } from "src/services";
import { PaginatedActiveAntsWarehouseCheckIns } from "src/services/activeAntsWarehouseCheckIns/activeAntsWarehouseCheckInAPIService";
import { QueryParamsMapper } from "src/mappers";
import { queryKeys } from "src/constants";
import { ActiveAntsWarehouseCheckIn } from "src/types/database";
import {
  DateTimeRange,
  WarehouseCheckInsPaginationOptions,
  WarehouseCheckInsFilters,
  UpdateWarehouseCheckInOpsNoteParams
} from "src/types";

export const useUpdateActiveAntsWarehouseCheckInOpsNote = (
  activeAntsWarehouseCheckInService: ActiveAntsWarehouseCheckInService,
  dateTimeRange?: DateTimeRange,
  filter?: WarehouseCheckInsFilters,
  paginationOptions?: WarehouseCheckInsPaginationOptions
) => {
  const queryClient = useQueryClient();
  const queryParams = QueryParamsMapper.getWarehouseCheckInsQueryParams(dateTimeRange, filter, paginationOptions);
  const [lastVariables, setLastVariables] = useState<UpdateWarehouseCheckInOpsNoteParams | null>(null);

  const updateInCache = (updated: ActiveAntsWarehouseCheckIn) => {
    if (dateTimeRange || filter || paginationOptions) {
      queryClient.setQueryData(
        queryKeys.paginatedActiveAntsWarehouseCheckIns(queryParams),
        (oldPaginated?: PaginatedActiveAntsWarehouseCheckIns) => ({
          ...oldPaginated,
          items: oldPaginated?.items?.map((c) =>
            c.id === updated.id && c.timestamp === updated.timestamp ? updated : c
          )
        })
      );
    }
    queryClient.setQueryData(queryKeys.activeAntsWarehouseCheckIn(updated.id, updated.timestamp), () => updated);
  };

  const {
    mutate: updateOpsNote,
    isPending: isUpdating,
    error: updateError,
    reset: resetUpdate
  } = useMutation<ActiveAntsWarehouseCheckIn, Error, UpdateWarehouseCheckInOpsNoteParams>({
    mutationFn: ({ id, timestamp, opsNote }) => activeAntsWarehouseCheckInService.updateOpsNote(id, timestamp, opsNote),
    onSuccess: updateInCache,
    onMutate: (variables) => setLastVariables(variables)
  });

  const retryUpdate = () => {
    if (lastVariables) updateOpsNote(lastVariables);
  };

  return { updateOpsNote, isUpdating, updateError, retryUpdate, resetUpdate };
};
